/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, isMsalConfigured } from '../auth/msalConfig';
import axios from 'axios';
import { setTokenGetter } from '../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Create context
const AuthContext = createContext(null);

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || null);
  
  const [user, setUser] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authEnabled, setAuthEnabled] = useState(true);

  // Check if authentication is enabled on the backend
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/status`);
        setAuthEnabled(response.data.authEnabled);
        
        if (!response.data.authEnabled) {
          // Auth disabled - fetch user info directly
          const userResponse = await axios.get(`${API_BASE_URL}/api/users/me`);
          setUser(userResponse.data.user);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to check auth status:', err);
        setAuthEnabled(true); // Default to enabled
      }
    };

    checkAuthStatus();
  }, []);

  // Fetch profile picture from Microsoft Graph
  const fetchProfilePicture = useCallback(async () => {
    if (!account || !instance) return;
    
    try {
      // Silent only — never popup for profile picture (fall back to initials instead)
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      }).catch(() => null);

      if (!tokenResponse?.accessToken) return;
      
      // Fetch photo from Graph API
      const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`
        }
      });
      
      if (photoResponse.ok) {
        const blob = await photoResponse.blob();
        // Convert to data URL instead of blob URL to comply with CSP
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfilePicture(reader.result);
        };
        reader.readAsDataURL(blob);
      }
      // 404 = no photo set, that's fine - we'll use initials
    } catch (err) {
      // Token or network error - that's okay, we'll use initials
    }
  }, [instance, account]);

  // Fetch user info from backend after authentication
  const fetchUserInfo = useCallback(async (idToken) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      setUser(response.data.user);
      setError(null);
      
      // Also try to fetch profile picture
      fetchProfilePicture();
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      setError('Failed to load user information. You may not have access.');
      setUser(null);
    }
  }, [fetchProfilePicture]);

  // In-flight dedup so concurrent callers share a single MSAL call
  const silentPromiseRef = useRef(null);
  const popupPromiseRef = useRef(null);

  const acquireSilent = useCallback((forceRefresh = false) => {
    if (!instance || !account) return Promise.reject(new Error('no_account'));
    if (!forceRefresh && silentPromiseRef.current) return silentPromiseRef.current;

    const silent = instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account,
      forceRefresh
    });
    // Cap silent at 6s — MSAL's iframe refresh can otherwise hang much longer
    // when the third-party cookie / SSO session is gone.
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('silent_timeout')), 6000)
    );
    const p = Promise.race([silent, timeout]).finally(() => {
      if (silentPromiseRef.current === p) silentPromiseRef.current = null;
    });
    if (!forceRefresh) silentPromiseRef.current = p;
    return p;
  }, [instance, account]);

  const acquirePopup = useCallback(() => {
    if (!instance || !account) return Promise.reject(new Error('no_account'));
    if (popupPromiseRef.current) return popupPromiseRef.current;
    const p = instance.acquireTokenPopup({
      scopes: loginRequest.scopes,
      account
    }).finally(() => {
      if (popupPromiseRef.current === p) popupPromiseRef.current = null;
    });
    popupPromiseRef.current = p;
    return p;
  }, [instance, account]);

  // Get ID token — silent with timeout, popup fallback, deduped across callers
  const getIdToken = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!authEnabled || !account) return null;

    try {
      const r = await acquireSilent(forceRefresh);
      return r.idToken;
    } catch (silentErr) {
      // Silent path is dead (expired RT, timeout, SSO cookie gone) — prompt interactively.
      try {
        const r = await acquirePopup();
        return r.idToken;
      } catch (popupErr) {
        if (popupErr?.errorCode !== 'user_cancelled') {
          console.warn('Token acquisition failed:', popupErr);
        }
        return null;
      }
    }
  }, [authEnabled, account, acquireSilent, acquirePopup]);

  // Set up the global API token getter
  useEffect(() => {
    if (authEnabled && account) {
      setTokenGetter(getIdToken);
    }
    return () => {
      setTokenGetter(null);
    };
  }, [authEnabled, account, getIdToken]);

  // Handle authentication state changes
  useEffect(() => {
    const handleAuth = async () => {
      if (!authEnabled) return;
      
      if (inProgress !== InteractionStatus.None) {
        return; // Wait for MSAL to finish
      }

      if (isAuthenticated && account) {
        setLoading(true);
        try {
          const token = await getIdToken();
          if (token) {
            await fetchUserInfo(token);
          } else {
            setError('Unable to get authentication token');
          }
        } catch (err) {
          console.error('Auth error:', err);
          setError('Authentication failed');
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    };

    handleAuth();
  }, [isAuthenticated, account, inProgress, getIdToken, fetchUserInfo, authEnabled]);

  // Login function
  const login = useCallback(async () => {
    if (!isMsalConfigured()) {
      setError('Azure AD is not configured. Please set VITE_AZURE_CLIENT_ID and VITE_AZURE_TENANT_ID.');
      return;
    }

    try {
      setError(null);
      await instance.loginPopup(loginRequest);
    } catch (err) {
      console.error('Login error:', err);
      if (err.errorCode !== 'user_cancelled') {
        setError(err.message || 'Login failed');
      }
    }
  }, [instance]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setUser(null);
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [instance]);

  // Single memoized axios instance — interceptors attached once per auth identity
  const authAxiosInstance = useMemo(() => {
    const inst = axios.create({ baseURL: API_BASE_URL });

    inst.interceptors.request.use(async (config) => {
      if (authEnabled) {
        const token = await getIdToken().catch(() => null);
        if (token) config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    inst.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && authEnabled && original && !original._authRetry) {
          original._authRetry = true;
          const token = await getIdToken({ forceRefresh: true }).catch(() => null);
          if (token) {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${token}`;
            return inst(original);
          }
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return inst;
  }, [authEnabled, getIdToken]);

  const authAxios = useCallback(() => authAxiosInstance, [authAxiosInstance]);

  // Context value
  const value = {
    // State
    user,
    profilePicture,
    loading,
    error,
    isAuthenticated: authEnabled ? (isAuthenticated && user !== null) : true,
    authEnabled,
    
    // Computed permissions
    permissions: user?.permissions || {
      canWrite: false,
      canManageUsers: false,
      isReadOnly: true
    },
    
    // Methods
    login,
    logout,
    getAccessToken: getIdToken, // Alias for compatibility
    authAxios,
    
    // Helper getters
    isAdmin: user?.role === 'admin',
    isReviewer: user?.role === 'reviewer',
    isViewer: user?.role === 'viewer',
    canWrite: user?.permissions?.canWrite || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
